import { useEffect, useState } from 'react';
import statsService from '../../services/statsService';

// spend-status is per campaign and takes 250–450 ms. The campaign list asks
// for one per active Evenly card, so requests are shared between consumers,
// kept for a while (pacing only moves when an hour closes) and capped to a
// few in flight at once.
export const SPEND_STATUS_TTL_MS = 5 * 60 * 1000;
export const SPEND_STATUS_MAX_CONCURRENT = 4;

const cache = new Map();
const queue = [];
let inFlight = 0;

const pump = () => {
    while (inFlight < SPEND_STATUS_MAX_CONCURRENT && queue.length > 0) {
        const { task, resolve, reject } = queue.shift();
        inFlight += 1;
        task()
            .then(resolve, reject)
            .finally(() => {
                inFlight -= 1;
                pump();
            });
    }
};

const schedule = (task) =>
    new Promise((resolve, reject) => {
        queue.push({ task, resolve, reject });
        pump();
    });

const getFreshEntry = (key) => {
    const entry = cache.get(key);
    if (!entry) return null;
    if (entry.result && Date.now() - entry.settledAt >= SPEND_STATUS_TTL_MS) {
        cache.delete(key);
        return null;
    }
    return entry;
};

/**
 * Loads the spend-status of a campaign through the shared cache and queue.
 * Never rejects: resolves with { data, error }, where error is the
 * normalized { status, code, message } thrown by the service.
 */
export const loadCampaignSpendStatus = (campaignId) => {
    const key = String(campaignId);
    const fresh = getFreshEntry(key);
    if (fresh) return fresh.promise;

    const entry = { promise: null, result: null, settledAt: 0 };
    const settle = (result) => {
        entry.result = result;
        entry.settledAt = Date.now();
        return result;
    };
    entry.promise = schedule(() =>
        statsService.getCampaignSpendStatus(campaignId)
    ).then(
        (data) => settle({ data, error: null }),
        (error) => settle({ data: null, error })
    );
    cache.set(key, entry);
    return entry.promise;
};

export const resetCampaignSpendStatusCache = () => {
    cache.clear();
    queue.length = 0;
    inFlight = 0;
};

const IDLE = { data: null, error: null, isLoading: false };

const hasCampaignId = (campaignId) =>
    campaignId !== null && campaignId !== undefined && campaignId !== '';

const initialState = (campaignId, enabled) => {
    if (!enabled || !hasCampaignId(campaignId)) return IDLE;
    const entry = getFreshEntry(String(campaignId));
    if (entry?.result) return { ...entry.result, isLoading: false };
    return { data: null, error: null, isLoading: true };
};

/**
 * spend-status of a campaign for a component. With `enabled` false it makes
 * no request and returns no data.
 * @returns {{ data: object|null, error: object|null, isLoading: boolean }}
 */
export default function useCampaignSpendStatus(
    campaignId,
    { enabled = true } = {}
) {
    const [state, setState] = useState(() => initialState(campaignId, enabled));

    useEffect(() => {
        setState(initialState(campaignId, enabled));
        if (!enabled || !hasCampaignId(campaignId)) return undefined;

        let isCurrent = true;
        loadCampaignSpendStatus(campaignId).then((result) => {
            // A response for a campaign the component no longer shows is dropped.
            if (isCurrent) setState({ ...result, isLoading: false });
        });
        return () => {
            isCurrent = false;
        };
    }, [campaignId, enabled]);

    return state;
}
